import "@saleor/utils/prototypeExtension/sortAlphabeticallyByKey";

import KeyboardArrowDown from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUp from "@material-ui/icons/KeyboardArrowUp";
import { useUser } from "@saleor/auth";
import { FIELD_TYPE } from "@saleor/changeOrderExport/components/OrderFilterSection/constant";
import {
  PermissionEnum,
  useFilterDomesticOrderConfirmationCompanyQuery,
  useFilterRequireAttentionMaterialLazyQuery,
  useGetSalesOrgBySoldToQuery,
  useScgpMeQuery,
  useSearchSuggestionDomesticSoldTosLazyQuery
} from "@saleor/graphql";
import { useFormik } from "formik";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";

const useDeliveryReportAdmin = ({ handleChangeFilter }) => {
  const { listCompany, company, user } = useUser();

  const isCustomer = useMemo(
    () =>
      !!user.userPermissions.find(
        e => e.code === PermissionEnum.ALL_REPORT_CUSTOMER
      ),
    [user.userPermissions]
  );

  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  const { data: scgpMe } = useScgpMeQuery({
    displayLoader: true
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      soldTo: isCustomer
        ? `${listCompany?.find(e => e.soldToCode === company)?.soldToCode} - ${
            listCompany?.find(e => e.soldToCode === company)?.soldToName
          }`
        : [],
      material: [],
      orderCreateDate: {
        gte: yesterday.toISOString().split("T")[0] || null,
        lte: today.toISOString().split("T")[0] || null
      },
      bu: "",
      saleOrganization: [],
      poNo: "",
      soNo: ""
      // status: filterAppState?.status || []
    },
    onSubmit: () => {
      const errorFields = [];
      if (!formik.values.soNo && !formik.values.soldTo.length) {
        errorFields.push({
          fieldName: "soldTo",
          value: "",
          error: "โปรดระบุ Sold-to"
        });
      }
      if (!formik.values.saleOrganization.length) {
        errorFields.push({
          fieldName: "saleOrganization",
          value: "",
          error: "โปรดระบุข้อมูล"
        });
      }
      if (!formik.values.bu) {
        errorFields.push({
          fieldName: "bu",
          value: "",
          error: "โปรดระบุข้อมูล BU"
        });
      }
      if (errorFields.length) {
        errorFields.forEach(e => {
          if (!e.value) {
            formik.setFieldError(e.fieldName, e.error);
          }
        });
      } else {
        handleChangeFilter({
          soldToCode: formik.values.soldTo?.[0]?.split("-")[0]?.trim() || "",
          matNo: formik.values.material?.[0] || "",
          bu: formik.values.bu,
          saleOrgCode: formik.values.saleOrganization,
          soNo: formik.values.soNo,
          poNo: formik.values.poNo,
          shippingPoint: "",
          deliveryDate: formik.values.orderCreateDate
        });
      }
    }
  });

  const intl = useIntl();
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [isClean, setIsClean] = useState<boolean>(false);

  // const [createGiftCard, createGiftCardOpts] = useGiftCardCreateMutation({
  //   onCompleted: data => {
  //     const errors = data?.giftCardCreate?.errors;

  //     notify(getGiftCardCreateOnCompletedMessage(errors, intl));

  //     if (!errors?.length) {
  //       setCardCode(data?.giftCardCreate?.giftCard?.code);
  //     }
  //   },
  //   refetchQueries
  // });

  const {
    data: confirmationConpanies
  } = useFilterDomesticOrderConfirmationCompanyQuery();

  const ArrowIconComponent = useMemo(
    () => (isOpen ? KeyboardArrowUp : KeyboardArrowDown),
    [isOpen]
  );

  const buList = useMemo(() => {
    const scgpBusList = [...(scgpMe?.scgpMe?.extendData?.scgpBus ?? [])];

    return scgpBusList.sortAlphabeticallyByKey("name").map(bu => ({
      label: bu.name,
      value: bu.name
    }));
  }, [scgpMe?.scgpMe?.extendData?.scgpBus]);

  const handleCleanForm = useCallback(() => {
    setIsClean(true);
    formik.resetForm();
    handleChangeFilter({});
    setTimeout(() => {
      formik.setValues({
        ...formik.values,
        bu: buList?.[0]?.value || ""
      });
      setIsClean(false);
    });
  }, [buList]);

  const handleCleanDates = useCallback(() => {
    formik.setValues({
      ...formik.values,
      orderCreateDate: {
        gte: null,
        lte: null
      }
    });
  }, [formik]);

  const [
    getMaterialAdminOrder,
    { data: fetchMaterialAdminOrder, loading: loadingMaterialAdminOrder }
  ] = useFilterRequireAttentionMaterialLazyQuery();

  const [
    getSoldToDataSuggestions,
    { data: fetchSoldToDataSuggestions, loading: loadingSoldToDataSuggestion }
  ] = useSearchSuggestionDomesticSoldTosLazyQuery({
    variables: {
      bu: formik.values.bu
    }
  });

  const {
    data: saleOrgs,
    refetch: refetchSalesOrg
  } = useGetSalesOrgBySoldToQuery({
    variables: {
      soldToCode: formik?.values?.soldTo?.[0]?.split("-")[0]?.trim() || ""
    },
    fetchPolicy: "no-cache"
  });

  const saleOrgChoices = useMemo(
    () =>
      (saleOrgs?.getSalesOrgBySoldTo || [])
        .filter(
          salesOrgItem => salesOrgItem.businessUnit.name === formik?.values?.bu
        )
        .map(salesOrgItem => ({
          label: `${salesOrgItem.code} - ${salesOrgItem.shortName}`,
          value: `${salesOrgItem.code}`
        })),
    [saleOrgs, formik?.values?.bu]
  );

  useEffect(() => {
    const bu = buList[0]?.value || "";
    setTimeout(() => formik.setFieldValue("bu", bu));
  }, [buList]);

  useEffect(() => {
    setTimeout(() => formik.setFieldValue("soldTo", new Array()));
  }, [formik?.values?.bu]);

  useEffect(() => {
    const mySalesOrg =
      scgpMe?.scgpMe?.extendData?.scgpSalesOrganizations?.map(x => x.code) ||
      [];
    const saleOrganization = saleOrgChoices
      ?.filter(salesOrg => mySalesOrg?.includes(salesOrg.value))
      .map(e => e.value);

    setTimeout(() =>
      formik.setFieldValue("saleOrganization", saleOrganization)
    );
  }, [saleOrgChoices]);

  const formData = useMemo(
    () => [
      {
        title: intl.formatMessage({
          defaultMessage: "Sold-to"
        }),
        subTitle: "(เลือกค้นหา Sold-to ได้ไม่เกิน 1 รายการ)",
        name: "soldTo",
        placeholder: intl.formatMessage({
          defaultMessage: "ค้นหา Sold to"
        }),
        required: true,
        type: !isCustomer
          ? FIELD_TYPE.SUGGESTION_INPUT
          : FIELD_TYPE.SINGLE_INPUT,
        disabled: isCustomer,
        maxLength: 35
      },
      {
        title: intl.formatMessage({
          defaultMessage: "Material Code / Description"
        }),
        subTitle: "(เลือกค้นหาได้ไม่เกิน 1 รายการ)",
        name: "material",
        placeholder: intl.formatMessage({
          defaultMessage: "ค้นหา Material Code / Description"
        }),
        type: FIELD_TYPE.SUGGESTION_INPUT
      },
      {
        title: intl.formatMessage({ defaultMessage: "วันที่ส่งมอบ" }),
        name: "orderCreateDate",
        type: FIELD_TYPE.DATEPICKER
      },
      {
        title: intl.formatMessage({ defaultMessage: "BU" }),
        name: "bu",
        type: FIELD_TYPE.SINGLE_SELECT,
        required: true,
        options: buList || []
      },
      {
        title: intl.formatMessage({ defaultMessage: "Sales Organization" }),
        name: "saleOrganization",
        placeholder: intl.formatMessage({
          defaultMessage: "เลือก Sales Organization"
        }),
        required: true,
        type: FIELD_TYPE.MULTIPLE_SELECT,
        options: saleOrgChoices,
        ignoreRerender: true
      },
      {
        title: intl.formatMessage({
          defaultMessage: "PO No."
        }),
        name: "poNo",
        placeholder: intl.formatMessage({
          defaultMessage: "ระบุ PO No."
        }),
        type: FIELD_TYPE.SINGLE_INPUT,
        maxLength: 35
      },
      {
        title: intl.formatMessage({
          defaultMessage: "S/O No."
        }),
        name: "soNo",
        placeholder: intl.formatMessage({
          defaultMessage: "ระบุ S/O No."
        }),
        type: FIELD_TYPE.SINGLE_INPUT,
        maxLength: 10
      }
    ],
    [confirmationConpanies, buList, saleOrgChoices]
  );

  const setDataFormik = data => {
    formik.setValues(data);
  };

  return useMemo(
    () => ({
      formik,
      setDataFormik,
      isOpen,
      setIsOpen,
      isClean,
      setIsClean,
      ArrowIconComponent,
      handleCleanForm,
      handleCleanDates,
      formData,
      refetchSalesOrg,
      fetchMaterialAdminOrder,
      getMaterialAdminOrder,
      loadingMaterialAdminOrder,
      getSoldToDataSuggestions,
      fetchSoldToDataSuggestions,
      loadingSoldToDataSuggestion
    }),
    [
      formik,
      isOpen,
      setIsOpen,
      isClean,
      setIsClean,
      ArrowIconComponent,
      handleCleanForm,
      handleCleanDates,
      formData,
      refetchSalesOrg,
      fetchMaterialAdminOrder,
      getMaterialAdminOrder,
      loadingMaterialAdminOrder,
      getSoldToDataSuggestions,
      fetchSoldToDataSuggestions,
      loadingSoldToDataSuggestion
    ]
  );
};

export default useDeliveryReportAdmin;
